'use client'

import { useEffect } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import posthog from 'posthog-js'

import shared from '@booking/index.module.scss'
import styles from './index.module.scss'
import SummaryBox from '@booking/components/SummaryBox'
import { useBookingState } from '@booking/state/booking.context'
import AttributionModal from '@booking/components/AttributionModal'

import ClockIcon from './assets/icon-clock.svg'
import EarphonesIcon from './assets/icon-earphones.svg'
import AssociateBox from '../../components/AssociateBox'

export default function ThankYou() {
  const t = useTranslations('booking')
  const { cart } = useBookingState()
  const allowAssociation = true

  useEffect(() => {
    if (!cart) return

    // Guard against re-firing on remount (e.g. a full page refresh while
    // parked on the thank-you stage), which would otherwise double-count
    // this booking as a conversion in the funnel/trend insights.
    const dedupeKey = `ph_booking_completed_${cart.id}`
    if (sessionStorage.getItem(dedupeKey)) return
    sessionStorage.setItem(dedupeKey, '1')

    posthog.capture('booking_completed', {
      cart_id: cart.id,
      cart_total: cart.total,
      ticket_count: cart.lines?.filter((l) => !l.voucher).length ?? 0,
      discount_applied: Boolean(cart.discount),
      session_id: cart.session?.id ?? null,
    })
  }, [cart?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!cart) {
    return <>{t('ticket.success.loadingCart', { default: 'Loading cart...' })}</>
  }

  return (
    <div className={cn(shared.box, styles.wrapper)}>
      <AttributionModal />
      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)}>
          <h2 className={cn(styles.title)}>{t('ticket.success.title')}</h2>
          <h3 className={cn(styles.subTitle)}>{t('ticket.success.getReady')}</h3>

          <div className={cn(styles.note)}>
            <ClockIcon className={styles.icon} />
            <div className={styles.content}>{t('ticket.success.note')}</div>
          </div>

          <div className={cn(styles.note)}>
            <EarphonesIcon className={styles.icon} />
            <div className={styles.content}>{t('ticket.success.advise')}</div>
          </div>

          {allowAssociation && <AssociateBox />}

          <div className={cn(styles.findUs)}>
            <h2>{t('ticket.success.howToFindUs')}</h2>
            <p>{t('ticket.success.locatedText')}</p>
            <p>
              <strong>{t('ticket.success.location')}</strong> | Rua Cais da Alfandega Velha |
              1300-598 Lisboa, Portugal
            </p>

            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3113.9077186433474!2d-9.199112185039212!3d38.69696396633947!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd1ecbce014d09e9%3A0x2753631d82d9d8c1!2sQuake%20-%20Centro%20Terramoto%20Lisboa!5e0!3m2!1sen!2suk!4v1650990738537!5m2!1sen!2suk"
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox />
        </div>
      </div>
    </div>
  )
}
